"use client";

import { RadarChart } from "@/components/charts/radar-chart";
import { RadarGrid } from "@/components/charts/radar-grid";
import { RadarAxis } from "@/components/charts/radar-axis";
import { RadarLabels } from "@/components/charts/radar-labels";
import { RadarArea } from "@/components/charts/radar-area";
import { SwipeScroll } from "@/components/charts/swipe-scroll";
import { ChartLegendHoverProvider } from "@/components/charts/chart-legend-hover";
import { useLegendClickSync } from "@/components/charts/use-legend-click-sync";
import {
  Legend,
  LegendItem,
  LegendLabel,
  LegendMarker,
} from "@/components/charts/legend";
import { cn } from "@/lib/utils";
import { ModuleChartRadarData } from "@/types/modules";
import { stripInlineMarkdown } from "@/utils/inline-markdown";
import { JSX, useMemo, useState } from "react";
import { CHART_SQUARE_SIZE } from "@/config/layout";
import { accentChartColors } from "../module-accent";
import { chartPaletteStyle } from "../imports-token-scope";

const DEFAULT_DATA: ModuleChartRadarData = {
  metrics: [
    { key: "speed", label: "Speed" },
    { key: "power", label: "Power" },
    { key: "accuracy", label: "Accuracy" },
    { key: "endurance", label: "Endurance" },
    { key: "reach", label: "Reach" },
  ],
  series: [
    {
      label: "Player A",
      values: { speed: 85, power: 70, accuracy: 78, endurance: 90, reach: 65 },
    },
    {
      label: "Player B",
      values: { speed: 68, power: 88, accuracy: 62, endurance: 74, reach: 82 },
    },
  ],
};

const CHART_SIZE = CHART_SQUARE_SIZE.radar;

export default function ModuleRadarChart({
  data = DEFAULT_DATA,
  showMarkers = false,
  grayscale,
  accent,
  className,
}: {
  data?: ModuleChartRadarData;
  showMarkers?: boolean;
  grayscale?: boolean;
  accent?: number;
  className?: string;
}): JSX.Element {
  const palette = accentChartColors(grayscale, accent);
  const series = useMemo(
    () =>
      data.series.map((s, i) => ({
        ...s,
        label: stripInlineMarkdown(s.label),
        color: s.color ?? palette[i % palette.length] ?? "#000",
      })),
    [data.series, palette],
  );
  const metrics = useMemo(
    () =>
      data.metrics.map((m) => ({
        ...m,
        label: stripInlineMarkdown(m.label),
      })),
    [data.metrics],
  );

  const [chartHovered, setChartHovered] = useState<number | null>(null);
  const [legendHovered, setLegendHovered] = useState<number | null>(null);
  const {
    activeIndex,
    handleSelect,
    setLabelRef,
    selectedOrigin,
    legendSelectedIndex,
    elementIndex,
    toggleElementIndex,
  } = useLegendClickSync();

  const radarHovered =
    chartHovered ?? legendHovered ?? elementIndex ?? activeIndex;

  return (
    <ChartLegendHoverProvider
      hoveredIndex={legendHovered}
      onHoverChange={setLegendHovered}
      selectedIndex={activeIndex}
      selectedOrigin={selectedOrigin}
      elementIndex={elementIndex}
      onElementToggle={toggleElementIndex}
    >
    <div className={cn("flex w-full flex-col gap-3", className)} style={chartPaletteStyle(grayscale, accent)}>
      <div className="flex w-full items-center justify-center">
        <RadarChart
          data={series}
          metrics={metrics}
          size={CHART_SIZE}
          levels={5}
          hoveredIndex={radarHovered}
          onHoverChange={setChartHovered}
        >
          <RadarGrid />
          <RadarAxis />
          <RadarLabels />
          {series.map((s, i) => (
            <RadarArea key={s.label} index={i} showPoints={showMarkers} />
          ))}
        </RadarChart>
      </div>

      <SwipeScroll className="w-full pb-1" fade="auto">
        <Legend
          items={series.map((s) => ({
            label: s.label,
            value: 0,
            color: s.color ?? "#000",
          }))}
          hoveredIndex={legendHovered}
          onHoverChange={setLegendHovered}
          selectedIndex={legendSelectedIndex}
          onSelect={handleSelect}
          onLabelRef={setLabelRef}
          className="flex flex-row gap-6"
        >
          <LegendItem className="flex shrink-0 items-center gap-2">
            <LegendMarker className="h-2.5 w-2.5" />
            <LegendLabel />
          </LegendItem>
        </Legend>
      </SwipeScroll>
    </div>
    </ChartLegendHoverProvider>
  );
}
